import { useEffect, useState, type FormEvent } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { genres } from "@/mocks/movies";

export default function SiteHeader() {
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [genreOpen, setGenreOpen] = useState(false);
  const [keyword, setKeyword] = useState("");

  useEffect(() => {
    setMenuOpen(false);
    setGenreOpen(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [menuOpen]);

  const handleSearch = (event: FormEvent) => {
    event.preventDefault();
    const q = keyword.trim();
    navigate(q ? `/search?q=${encodeURIComponent(q)}` : "/search");
    setMenuOpen(false);
  };

  const isActive = (path: string) => {
    if (path === "/") return location.pathname === "/";
    return location.pathname.startsWith(path);
  };

  const navLinkClass = (path: string) =>
    `flex items-center gap-1.5 px-3 py-2 text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
      isActive(path)
        ? "text-primary-600"
        : "text-foreground-700 hover:text-primary-600"
    }`;

  return (
    <header className="sticky top-0 z-50 border-b border-background-200 bg-background-50/95 backdrop-blur">
      <div className="mx-auto flex h-16 w-full items-center gap-4 px-4 md:px-6">
        <Link to="/" className="flex items-center gap-2.5 cursor-pointer" aria-label="免费电影在线观看 首页">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-500 text-lg text-background-50">
            <i className="ri-movie-2-fill" />
          </span>
          <span className="flex flex-col leading-none">
            <span className="font-heading text-base font-bold text-foreground-950">
              免费电影
            </span>
            <span className="mt-0.5 text-[11px] text-foreground-500">
              在线观看
            </span>
          </span>
        </Link>

        <nav className="ml-2 hidden items-center md:flex">
          <Link to="/" className={navLinkClass("/")}>
            <i className="ri-home-5-line text-base" />
            首页
          </Link>
          <div
            className="relative"
            onMouseEnter={() => setGenreOpen(true)}
            onMouseLeave={() => setGenreOpen(false)}
          >
            <button
              type="button"
              onClick={() => setGenreOpen((v) => !v)}
              className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                isActive("/category")
                  ? "text-primary-600"
                  : "text-foreground-700 hover:text-primary-600"
              }`}
            >
              <i className="ri-apps-2-line text-base" />
              分类
              <i className={`ri-arrow-down-s-line text-base transition-transform ${genreOpen ? "rotate-180" : ""}`} />
            </button>
            {genreOpen && (
              <div className="absolute left-0 top-full z-50 w-[420px] rounded-lg border border-background-200 bg-background-50 p-3 shadow-sm">
                <div className="grid grid-cols-2 gap-1">
                  {genres.map((genre) => (
                    <Link
                      key={genre.slug}
                      to={`/category/${genre.slug}`}
                      className="flex items-center gap-2 rounded-md px-3 py-2 text-sm text-foreground-700 transition-colors hover:bg-primary-50 hover:text-primary-700 cursor-pointer"
                    >
                      <span className="flex h-7 w-7 items-center justify-center rounded-md bg-primary-100 text-primary-700">
                        <i className={genre.icon} />
                      </span>
                      {genre.name}电影
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
          <Link to="/search" className={navLinkClass("/search")}>
            <i className="ri-search-line text-base" />
            搜索
          </Link>
          <Link to="/about" className={navLinkClass("/about")}>
            <i className="ri-information-line text-base" />
            关于
          </Link>
        </nav>

        <form
          onSubmit={handleSearch}
          className="ml-auto hidden w-64 items-center gap-2 rounded-full border border-background-300 bg-background-100 px-3 py-1.5 transition-colors focus-within:border-primary-400 lg:flex"
        >
          <i className="ri-search-line text-base text-foreground-500" />
          <input
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            type="text"
            placeholder="搜索电影、导演、演员..."
            className="w-full bg-transparent text-sm text-foreground-800 outline-none placeholder:text-foreground-400"
          />
        </form>

        <button
          type="button"
          onClick={() => setMenuOpen((v) => !v)}
          aria-label="打开菜单"
          className="ml-auto flex h-10 w-10 items-center justify-center rounded-md border border-background-300 text-foreground-800 md:hidden cursor-pointer"
        >
          <i className={menuOpen ? "ri-close-line text-xl" : "ri-menu-line text-xl"} />
        </button>
      </div>

      {menuOpen && (
        <div className="border-t border-background-200 bg-background-50 md:hidden animate-fade-in">
          <div className="px-4 py-4">
            <form
              onSubmit={handleSearch}
              className="flex items-center gap-2 rounded-full border border-background-300 bg-background-100 px-3 py-2 focus-within:border-primary-400"
            >
              <i className="ri-search-line text-base text-foreground-500" />
              <input
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                type="text"
                placeholder="搜索电影、导演、演员..."
                className="w-full bg-transparent text-sm text-foreground-800 outline-none placeholder:text-foreground-400"
              />
              <button
                type="submit"
                className="flex h-8 w-8 items-center justify-center rounded-full bg-primary-500 text-background-50 cursor-pointer"
                aria-label="搜索"
              >
                <i className="ri-arrow-right-line" />
              </button>
            </form>

            <nav className="mt-4 flex flex-col">
              {[
                { to: "/", label: "首页", icon: "ri-home-5-line" },
                { to: "/search", label: "搜索", icon: "ri-search-line" },
                { to: "/about", label: "关于", icon: "ri-information-line" },
              ].map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  className="flex items-center gap-2 rounded-md px-2 py-2.5 text-sm font-medium text-foreground-800 hover:bg-background-100 cursor-pointer"
                >
                  <i className={`${item.icon} text-base text-foreground-500`} />
                  {item.label}
                </Link>
              ))}
            </nav>

            <div className="mt-3 border-t border-background-200 pt-3">
              <p className="px-2 pb-2 text-xs font-semibold uppercase tracking-wide text-foreground-500">
                电影分类
              </p>
              <div className="grid grid-cols-2 gap-1">
                {genres.map((genre) => (
                  <Link
                    key={genre.slug}
                    to={`/category/${genre.slug}`}
                    className="flex items-center gap-2 rounded-md px-2 py-2 text-sm text-foreground-700 hover:bg-background-100 cursor-pointer"
                  >
                    <i className={`${genre.icon} text-primary-600`} />
                    {genre.name}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}